import React, { useEffect, useMemo, useState, lazy, createElement } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, LockIcon, CheckCircleIcon, BrainIcon, AwardIcon, LaptopIcon, DollarSignIcon, GraduationCapIcon, ChurchIcon, RouteIcon, UsersIcon, MedalIcon, ShieldCheckIcon } from 'lucide-react';
import { TrackingNoticeLinks } from '../components/TrackingNoticeLinks';
import { getUniversityBySlug, getProgramBySlug } from '../data/universityData';
import { useMetaTags } from '../hooks/useMetaTags';
import { getPublisherParams } from '../utils/publisherParams';
import { trackFormSubmit } from '../utils/trackFormSubmit';
import { getMetaCookies, resolveSub3 } from '../utils/metaCookies';
import { validateAddress, lookupZip } from '../utils/addressValidation';
import { injectLeadCertificationScripts, readCertificationFields, waitForTrustedFormCert } from '../utils/leadCertificationScripts';
import { fetchClientIp, prefetchClientIp } from '../utils/clientIp';
import { LeadTrackingFields } from '../components/LeadTrackingFields';
import { getUrlPassthroughParams } from '../utils/urlPassthrough';
// Form value → API value mappings
const educationToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    'high-school': 1,
    'some-high-school': 2,
    ged: 3,
    'some-college': 5,
    associates: 5,
    bachelors: 7,
    masters: 7,
    doctorate: 7
  };
  return map[v] || 9;
};
const startTimeframeToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    immediately: 1,
    '1-3-months': 2,
    '4-6-months': 3,
    '3-6-months': 3,
    '6-12-months': 4,
    '1-year-plus': 4,
    undecided: 6
  };
  return map[v] || 6;
};
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'mobile';
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  return 'desktop';
};
export function AdvertiserTemplatePage() {
  const {
    collegeSlug,
    programSlug
  } = useParams<{
    collegeSlug: string;
    programSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const university = collegeSlug ? getUniversityBySlug(collegeSlug) : undefined;
  const initialProgram = university && programSlug ? getProgramBySlug(university, programSlug) : null;
  // Capture optional tracking params from the URL once at mount.
  // sub1/sub2/sub3 + the rest of the documented optional fields can all
  // be passed in via query string and will be forwarded on submit.
  const trackingParams = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return {
      sub1: q.get('sub1') || '',
      sub2: q.get('sub2') || '',
      sub3: q.get('sub3') || '',
      userIP: q.get('userIP') || '',
      webInitiatingURL: q.get('webInitiatingURL') || document.referrer || '',
      webLandingURL: q.get('webLandingURL') || window.location.href,
      inventoryType: q.get('inventoryType') || '',
      mediaType: q.get('mediaType') || '1'
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Inject LeadID + TrustedForm on mount, and never tear them down.
  // TrustedForm builds its certificate by observing the visitor's whole
  // session, so deferring the tag to idle time (or removing it on unmount)
  // shortens or restarts that observation and can leave the certificate
  // field empty at submit time. The IP prefetch stays deferred.
  useEffect(() => {
    injectLeadCertificationScripts();
    const handle = window.setTimeout(prefetchClientIp, 1200);
    return () => window.clearTimeout(handle);
  }, []);
  const metaDescription = university ? initialProgram ? `Explore the ${initialProgram.degreeLevel ? `${initialProgram.degreeLevel} ` : ''}${initialProgram.name} program at ${university.name} and request information about your next steps.` : `Explore online degree programs at ${university.name} and request information about options that align with your educational goals.` : 'The requested university program could not be found. Explore accredited online degree programs on FindOnlineDegrees.';
  useMetaTags({
    title: university ? `${university.name} | Request Information` : 'Program Not Found',
    description: metaDescription,
    ogImage: university?.landingContent?.communityImage?.url,
    ogType: 'website',
    // Partner advertiser pages are paid lead-gen surfaces; keep them out
    // of the index so they don't compete with the brand site for SEO.
    robots: 'noindex, follow'
  });
  // Campus-aware advertisers (e.g. SJVC) use a step 0 to look up the user's
  // campus by zip before showing program options.
  const hasCampusFlow = Boolean(university?.campusZipLookup);
  const firstStep = hasCampusFlow ? 0 : 1;
  const totalSteps = hasCampusFlow ? 5 : 4;
  const [step, setStep] = useState(firstStep);
  const [formData, setFormData] = useState({
    degreeLevel: initialProgram?.degreeLevel || '',
    areaOfStudy: initialProgram?.category || '',
    program: initialProgram?.name || '',
    highestEducation: '',
    startDate: '',
    hsGradYear: '',
    zipCode: '',
    city: '',
    state: '',
    streetAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false,
    consentSms: false,
    campusId: 0,
    campusName: ''
  });
  const [error, setError] = useState('');
  const [campusLookupError, setCampusLookupError] = useState('');
  // True while the ZIP is being verified on the address step.
  const [addressChecking, setAddressChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Auto-advance Step 2
  useEffect(() => {
    if (step === 2 && formData.highestEducation && formData.startDate && formData.hsGradYear) {
      const timer = setTimeout(() => {
        setError('');
        setStep(3);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.highestEducation, formData.startDate, formData.hsGradYear, step]);
  // ZIP code lookup — shares the cached lookup used by address validation, so
  // verifying on Continue doesn't re-request a ZIP already resolved here.
  useEffect(() => {
    if (formData.zipCode.length !== 5) return;
    let active = true;
    lookupZip(formData.zipCode).then(place => {
      if (!active || !place) return;
      setFormData(prev => ({
        ...prev,
        city: place.city,
        state: place.state
      }));
    });
    return () => {
      active = false;
    };
  }, [formData.zipCode]);
  if (!university) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-853" data-mp="2yiktc530xfnx">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center" data-id="element-854" data-mp="2yggi122spedm">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-855" data-mp="2okdepsw7j9p3">
            Program Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-856" data-mp="2tj7u0kuyug4g">
            We couldn't find the specific program or university you're looking
            for.
          </p>
          <button onClick={() => navigate('/')} className="w-full py-3 bg-gray-900 text-white rounded-lg font-bold hover:bg-gray-800 transition-colors" data-id="element-857" data-mp="2et0iyq7afzds">
            Return Home
          </button>
        </div>
      </div>;
  }
  const updateField = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  const handleContinue = async () => {
    if (step === 0) {
      if (!formData.zipCode || formData.zipCode.length !== 5) {
        setCampusLookupError('Please enter your 5-digit ZIP code to continue.');
        return;
      }
      if (!formData.campusId) {
        setCampusLookupError(`Sorry, that ZIP code isn't served by a ${university.shortName} campus. Please check your ZIP and try again.`);
        return;
      }
      const campusPrograms = university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
      const preselectStillValid = !!initialProgram && campusPrograms.some(p => p.name === initialProgram.name);
      setFormData(prev => ({
        ...prev,
        degreeLevel: preselectStillValid ? initialProgram!.degreeLevel || '' : '',
        areaOfStudy: preselectStillValid ? initialProgram!.category : '',
        program: preselectStillValid ? initialProgram!.name : ''
      }));
      setCampusLookupError('');
      setStep(1);
      return;
    }
    if (step === 1) {
      if (university.singleProgramSelect) {
        if (!formData.program) {
          setError('Please select a program to continue.');
          return;
        }
      } else if (!formData.degreeLevel || !formData.areaOfStudy || !formData.program) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!formData.highestEducation || !formData.startDate || !formData.hsGradYear) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      // Verify the street line looks like an address and that the ZIP exists
      // before the lead is posted. A lookup outage never blocks the user.
      setAddressChecking(true);
      const check = await validateAddress({
        streetAddress: formData.streetAddress,
        zipCode: formData.zipCode,
        state: formData.state
      });
      setAddressChecking(false);
      if (!check.valid) {
        setError(check.message);
        return;
      }
      if (check.place) {
        setFormData(prev => ({
          ...prev,
          city: prev.city || check.place!.city,
          state: prev.state || check.place!.state
        }));
      }
      setStep(4);
    }
  };
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) return formData.phone;
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  };
  const handleSubmit = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (formData.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!university.consentOnSubmit && !formData.consent) {
      setError('Please agree to the consent terms.');
      return;
    }
    if (university.tcpaTextSms && !formData.consentSms) {
      setError('Please check both consent boxes to continue.');
      return;
    }
    setIsSubmitting(true);
    // Read tracking values populated by the LeadID + TrustedForm scripts.
    // Scans every matching field, since TrustedForm appends its own input
    // rather than reusing the placeholder we render, and waits briefly in case
    // the certificate hasn't been issued yet.
    await waitForTrustedFormCert();
    const certification = readCertificationFields();
    const leadIdToken = certification.leadIdToken || generateUUID();
    const trustedFormUrl = certification.trustedFormUrl;
    const trustedFormPingUrl = certification.trustedFormPingUrl;
    // Build the full lead payload per advertiser parameter spec.
    // Includes: required advertiser identifiers, mapped form values,
    // PII, and optional tracking parameters.
    const payload: Record<string, string> = {
      // Everything the landing URL carried, forwarded verbatim. Listed first
      // so the resolved values below always take precedence.
      ...getUrlPassthroughParams(location.search),
      // Required advertiser identifiers. URL params (mPubGroupID, or
      // mPubID + mPubCampaignID) override the per-university config values
      // when present; otherwise fall back to the university config.
      ...getPublisherParams(location.search, {
        mPubID: university.mPubID || '',
        mPubCampaignID: university.mPubCampaignID || ''
      }),
      mBrandID: university.mBrandID || '',
      hpRequestId: generateUUID(),
      mSessionID: `s-${generateUUID()}`,
      leadIdToken,
      // Mapped form values. When the partner provides a numeric program
      // catalog (programId), post that ID as mProgram; otherwise fall back
      // to the program name.
      degreeInterest: formData.degreeLevel,
      areaStudy: formData.areaOfStudy,
      mProgram: (() => {
        const matched = programsForThisFlow.find(p => p.name === formData.program);
        if (matched?.mProgramValue) return matched.mProgramValue;
        return matched?.programId ? String(matched.programId) : formData.program;
      })(),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      gradYear: formData.hsGradYear,
      // PII
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ''),
      address: formData.streetAddress,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      campusLocation: formData.campusId ? String(formData.campusId) : '',
      smsConsent: formData.consentSms ? '1' : '0',
      // Optional tracking
      ipAddress: await fetchClientIp(),
      useragent: navigator.userAgent,
      sub1: trackingParams.sub1,
      sub2: trackingParams.sub2,
      // sub3 carries the Meta match keys ("<fbc>_<fbp>") for the Conversions
      // API when the Pixel cookies are present.
      sub3: resolveSub3(window.location.search) || trackingParams.sub3,
      userIP: trackingParams.userIP,
      webInitiatingURL: trackingParams.webInitiatingURL,
      webLandingURL: trackingParams.webLandingURL,
      trustedFormUrl,
      trustedFormPingUrl,
      inventoryType: trackingParams.inventoryType,
      deviceType: getDeviceType(),
      mediaType: trackingParams.mediaType
    };
    // Meta match keys for server-side (CAPI) attribution.
    const {
      fbp,
      fbc
    } = getMetaCookies();
    if (fbp) payload.fbp = fbp;
    if (fbc) payload.fbc = fbc;
    console.log('[Advertiser Lead] payload:', payload);
    // Report the submission to GTM once (and only once) the lead has been
    // sent. `leadIdToken` doubles as the dedupe id for Meta CAPI.
    let reported = false;
    const reportSubmission = () => {
      if (reported) return;
      reported = true;
      trackFormSubmit({
        formId: 'university-request-info',
        pageUrl: window.location.pathname,
        leadId: leadIdToken
      });
    };
    // Host-and-post: the advertiser provides a posting endpoint per partner.
    // Configure it via university.leadPostUrl. If unset, no post is fired
    // (the payload is still logged above for QA / verification).
    const postBase = university.leadPostUrl || '';
    if (postBase) {
      const qs = new URLSearchParams(payload).toString();
      const fullUrl = `${postBase}?${qs}`;
      console.log('[Advertiser Lead] POST URL:', fullUrl);
      try {
        const pixel = new Image();
        pixel.onload = reportSubmission;
        pixel.src = fullUrl;
      } catch (err) {
        console.error('[Advertiser Lead] post error:', err);
      }
    } else {
      console.log('[Advertiser Lead] No leadPostUrl configured for this advertiser — skipping post.');
    }
    // The conversion is reported through GTM (dataLayer `form_submission`)
    // once the lead post is confirmed — no Meta Pixel event is fired here.
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      // Advertisers without a configured posting endpoint never produce a
      // pixel load, so confirm on the success state instead.
      reportSubmission();
      // Partners with a dedicated thank-you page keep the user there; the
      // fallback card is transient, so it bounces home.
      if (!university.thankYou) {
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    }, 1000);
  };
  // Derived program list — honors campus-aware advertisers (filters to the
  // programs available at the user's matched campus).
  const programsForThisFlow = hasCampusFlow && formData.campusId && university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
  // Derived options for Step 1
  const uniqueAreas = Array.from(new Set(programsForThisFlow.filter(p => !formData.degreeLevel || p.degreeLevel === formData.degreeLevel).map(p => p.category)));
  const availablePrograms = programsForThisFlow.filter(p => (!formData.degreeLevel || p.degreeLevel === formData.degreeLevel) && (!formData.areaOfStudy || p.category === formData.areaOfStudy));
  const currentYear = new Date().getFullYear();
  // Years run back to 1990; everything earlier is collapsed into the
  // "1989 or earlier" option, which posts 1989.
  const years = Array.from({
    length: currentYear - 1989
  }, (_, i) => currentYear - i);
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  if (isSuccess) {
    // Partners with thank-you copy get a branded confirmation page that
    // stays put; everyone else keeps the brief redirect card.
    if (university.thankYou) {
      const ty = university.thankYou;
      return <div className="min-h-screen font-sans flex flex-col bg-gray-50" data-id="element-858" data-mp="1l6htdmu1oyod">
          <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-859" data-mp="1p8ctwp45j813">
            <div className="max-w-7xl mx-auto flex items-center justify-center" data-id="element-860" data-mp="1p601fi0rpf9s">
              {university.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-861" data-mp="30n85g27i4tdf" /> : <span className="text-xl font-bold" style={{
              color: university.primaryColor
            }} data-id="element-862" data-mp="kzm87y25qp1d">
                  {university.name}
                </span>}
            </div>
          </header>

          <main className="flex-grow" data-id="element-863" data-mp="69n65k7hetgm">
            <section className="px-4 py-16 md:py-24" style={{
            backgroundColor: university.primaryColor
          }} data-id="element-864" data-mp="6ag7u6rl2srw">
              <div className="max-w-2xl mx-auto text-center text-white" data-id="element-865" data-mp="3tecvvzflvnzv">
                <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-6" data-id="element-866" data-mp="3temm9o4kbx63">
                  <CheckCircleIcon className="w-9 h-9 text-white" data-id="element-867" data-mp="3ohmg7d7pquez" />
                </div>
                <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-4" data-id="element-868" data-mp="1rkx42z7s833b">
                  {formData.firstName ? `${ty.heading}, ${formData.firstName}` : ty.heading}
                </h1>
                <p className="text-base md:text-lg text-white/85 leading-relaxed" data-id="element-869" data-mp="2ayv72ztn0v8v">
                  {ty.body}
                </p>
              </div>
            </section>

            {ty.nextSteps && ty.nextSteps.length > 0 && <section className="bg-white px-4 py-14 md:py-20" data-id="element-870" data-mp="218sbqsz1n57d">
                <div className="max-w-3xl mx-auto" data-id="element-871" data-mp="1wb0ctezzeito">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-8 text-center" data-id="element-872" data-mp="1wbtjkfyxx8bt">
                    What happens next
                  </h2>
                  <ol className="space-y-6" data-id="element-873" data-mp="1n90w6nsvpgr9">
                    {ty.nextSteps.map((s, i) => <li key={s} className="flex items-start gap-4" data-id="element-874" data-mp="1ibiwlo0r4dvx" data-mpi={i}>
                        <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold" style={{
                    backgroundColor: `${university.ctaColor}2e`,
                    color: university.primaryColor
                  }} data-id="element-875" data-mp="3hntr0klxu3i5">
                          {i + 1}
                        </span>
                        <p className="text-base text-gray-700 leading-relaxed pt-1" data-id="element-876" data-mp="3tzdwt6pz75v0">
                          {s}
                        </p>
                      </li>)}
                  </ol>
                  <div className="mt-10 text-center" data-id="element-877" data-mp="31aym2wdvn9ph">
                    <button onClick={() => navigate('/')} className="px-7 py-3.5 font-bold text-sm uppercase tracking-wider rounded-md transition-colors" style={{
                  backgroundColor: university.ctaColor,
                  color: university.ctaTextColor || '#ffffff'
                }} data-id="element-878" data-mp="367yq71r622dy">
                      Explore More Schools
                    </button>
                  </div>
                </div>
              </section>}
          </main>

          <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs" data-id="element-879" data-mp="2rlen9tn6hj12">
            <div className="max-w-5xl mx-auto space-y-2" data-id="element-880" data-mp="2mmlay4jmny2h">
              <p data-id="element-881" data-mp="30j8l590lckqc">
                © {new Date().getFullYear()} {university.name}. All rights
                reserved.
              </p>
              <TrackingNoticeLinks data-id="element-882" data-mp="2wgk96sgkyxp5" />
            </div>
          </footer>
        </div>;
    }
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-883" data-mp="28qpwqc06bb5f">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 max-w-md w-full text-center" data-id="element-884" data-mp="23rwl6p4g5u7w">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-885" data-mp="2hn8mjfd3yixs">
            <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-886" data-mp="1p4v0se51qnf2" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-887" data-mp="1p2qlz15m0400">
            Thanks, {formData.firstName}!
          </h2>
          <p className="text-gray-600" data-id="element-888" data-mp="1azunsh6zu2p4">
            {university.name} will reach out shortly. Redirecting you to the
            homepage...
          </p>
        </div>
      </div>;
  }
  // Dynamic hero title — show the selected program once chosen, otherwise a generic prompt.
  // On the first step (entry view), prefer the partner's high-CTR landing headline if provided.
  const isEntryStep = step === firstStep;
  // Once the user has chosen both a program and a degree level, surface the
  // degree in the H1 (e.g. "Bachelor's in Early Childhood Education").
  const selectedProgramTitle = formData.program && formData.degreeLevel ? `${formData.degreeLevel} in ${formData.program}` : formData.program || initialProgram?.name;
  const heroTitle = isEntryStep && university.landingContent ? university.landingContent.headline : selectedProgramTitle || `Find Your ${university.shortName} Program`;
  // Show the fuller landing subheadline on every step (entry and beyond) so
  // the program-selection / later steps aren't left with just the short
  // mission line. Falls back to the mission when no landing copy exists.
  const heroDescription = university.landingContent?.subheadline !== undefined ? university.landingContent.subheadline : university.mission;
  const featureIconMap: Record<string, any> = {
    BrainIcon,
    AwardIcon,
    LaptopIcon,
    DollarSignIcon,
    GraduationCapIcon,
    ChurchIcon,
    RouteIcon,
    UsersIcon,
    MedalIcon,
    ShieldCheckIcon
  };
  return <div className="min-h-screen font-sans flex flex-col bg-gray-50" data-id="element-889" data-mp="17j9oq7zkcz5h">
      {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
      <LeadTrackingFields data-id="element-890" data-mp="xkssavhuvsc1" />

      {/* White top nav bar with centered logo */}
      <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-891" data-mp="xmwth5oded8m">
        <div className="max-w-7xl mx-auto flex items-center justify-center relative" data-id="element-892" data-mp="3nb5yjvap9wzh">
          {university.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-893" data-mp="3id4asx2bx9ho" /> : <span className="text-xl font-bold" style={{
          color: university.primaryColor
        }} data-id="element-894" data-mp="27qwxolet4287">
              {university.name}
            </span>}
          <div className="hidden md:flex items-center gap-1.5 text-gray-400 text-xs font-medium absolute right-0" data-id="element-895" data-mp="2hhhqfs7sucj7">
            <LockIcon className="w-3.5 h-3.5" data-id="element-896" data-mp="2lh0cwsf9qvo5" />
            Secure
          </div>
        </div>
      </header>

      {/* Branded main wrapper — neutral surface for the supporting sections */}
      <main className="flex-grow bg-white" data-id="element-897" data-mp="2vfg3muadw2x4">
        {/* HERO — full-bleed brand color, optionally overlaid with a partner image */}
        <section className="relative py-12 md:py-20 px-3 sm:px-4 bg-cover bg-center" style={{
        backgroundColor: university.primaryColor,
        backgroundImage: university.heroBackgroundImage && !university.heroImageFlip ? `url(${university.heroBackgroundImage})` : undefined
      }} data-id="element-898" data-mp="w57xodapbydi">
          {/* Mirrored hero art: rendered as an element so it can be flipped,
           moving the subjects clear of the form card on the right. */}
          {university.heroBackgroundImage && university.heroImageFlip && <div aria-hidden="true" className="absolute inset-0 overflow-hidden" data-id="element-899" data-mp="1byjwqmqxxfnr">
              <img src={university.heroBackgroundImage} alt="" className="w-full h-full object-cover scale-x-[-1]" data-id="element-900" data-mp="1lq6el1xsaczh" />
            </div>}

          {/* Opt-in scrim for hero photos that are too bright for white copy.
           Partners whose hero art is already dark leave it off. */}
          {university.heroBackgroundImage && university.heroImageScrim && <div aria-hidden="true" className="absolute inset-0" style={{
          backgroundColor: university.primaryColor,
          opacity: 0.8
        }} data-id="element-901" data-mp="2acdpuwxsl5mu" />}

          {/* Form column is given the larger share: it's the reason the page
           exists, and the extra measure keeps the TCPA text readable. */}
          <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-start" data-id="element-902" data-mp="1qnd4fiq4je08">
            {/* Left: Branded hero copy — on mobile the whole column sits
             BELOW the form, so the first question is the first thing on the
             page and the school name reads as the answer to "who is this?" */}
            <div className="text-white lg:pt-8 order-2 lg:order-1" data-id="element-903" data-mp="1mm9pqzjtbo0l">
              <p className="text-sm lg:text-base font-semibold mb-3 lg:mb-4 text-white/90" data-id="element-904" data-mp="17o7k8jt80sgp">
                {university.name}
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6" data-id="element-905" data-mp="1cmskgj8h3c55">
                {heroTitle}
              </h1>
              {heroDescription && <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-lg" data-id="element-906" data-mp="1o4rs80t5m8k6">
                  {heroDescription}
                </p>}
              {university.landingContent?.heroHighlights && university.landingContent.heroHighlights.length > 0 && <ul className="mt-6 space-y-2.5 max-w-lg" data-id="element-907" data-mp="1980strjhcwuk">
                    {university.landingContent.heroHighlights.map(h => <li key={h} className="flex items-start gap-2.5 text-sm md:text-base text-white/90" data-id="element-908" data-mp="10e5vv6siicq0">
                        <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-white/70" data-id="element-909" data-mp="vdhxg01yetlr" />
                        <span data-id="element-910" data-mp="qi2jz97uyzpv">{h}</span>
                      </li>)}
                  </ul>}
            </div>

            {/* Right: Form card — appears directly under the H1 on mobile
             (above the fold), to the right on desktop. */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-5 sm:p-6 md:p-8 lg:p-10 order-1 lg:order-2" data-id="element-911" data-mp="gagi59h1qwuk">
              {/* Progress Indicator */}
              <div className="mb-6" data-id="element-912" data-mp="q9fsqxldh2k7">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center" data-id="element-913" data-mp="q8ee6j55dxxq">
                  {university.formHeading || 'Take the next step in your education.'}
                </h2>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-914" data-mp="3tp3ehne50bey">
                  <div className="h-full transition-all duration-300" style={{
                  width: `${(step - firstStep + 1) / totalSteps * 100}%`,
                  backgroundColor: university.ctaColor
                }} data-id="element-915" data-mp="2jlgah4ytg0h" />
                </div>
              </div>

              {/* Step 0: Campus lookup by zip (campus-aware advertisers only) */}
              {step === 0 && hasCampusFlow && <div className="space-y-6" data-id="element-916" data-mp="2o8c76eb1o6ys">
                  <div data-id="element-917" data-mp="2o714khtc5599">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-918" data-mp="29ccxx0c780s2">
                      ZIP Code
                    </label>
                    <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setCampusLookupError('');
                  // Auto-resolve campus when the zip becomes valid.
                  // If no match, leave the dropdown alone so the
                  // user can still pick manually.
                  if (val.length === 5 && university.campusZipLookup) {
                    const match = university.campusZipLookup(val);
                    if (match) {
                      setFormData(prev => ({
                        ...prev,
                        zipCode: val,
                        campusId: match.id,
                        campusName: match.name
                      }));
                      return;
                    }
                  }
                  updateField('zipCode', val);
                }} placeholder="e.g. 93291" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-lg" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-919" data-mp="2e9mnqh3i2egf" />
                  </div>

                  {/* Campus readout is only meaningful for partners with more
                   than one campus. Online-only schools (CCU) just need the
                   ZIP, so a one-option disabled dropdown is noise. */}
                  {(university.landingContent?.campusList?.length || 0) > 1 && <div data-id="element-920" data-mp="1b3iyetf2cm51">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-921" data-mp="1b3shdx1oxt18">
                        Campus
                      </label>
                      <select value={formData.campusId || ''} disabled aria-readonly="true" tabIndex={-1} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg text-gray-700 appearance-none cursor-not-allowed" style={selectStyle as any} data-id="element-922" data-mp="1l05ihfzkf960">
                        <option value="" data-id="element-923" data-mp="3vthw8a2e9etx">
                          -- Enter ZIP to match a campus --
                        </option>
                        {(university.landingContent?.campusList || []).map(c => <option key={c.id} value={c.id} data-id="element-924" data-mp="3ejdzta8ticid">
                              {c.name} Campus
                            </option>)}
                      </select>
                      <p className="mt-2 text-xs text-gray-500" data-id="element-925" data-mp="b2xec3fiheez">
                        Your campus is automatically determined by your ZIP
                        code.
                      </p>
                    </div>}

                  {campusLookupError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-926" data-mp="3t8tlwcxeltrq">
                      <p className="text-red-600 text-sm" data-id="element-927" data-mp="2luqjlt2xedbo">
                        {campusLookupError}
                      </p>
                    </div>}
                </div>}

              {/* Step 1: Program Selection — single dropdown variant */}
              {step === 1 && university.singleProgramSelect && <div className="space-y-6" data-id="element-928" data-mp="ebx8xf52oab3">
                  <div data-id="element-929" data-mp="3vrncjr65acl3">
                    <label htmlFor="program-select" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-930" data-mp="3vseuhz6tkbsw">
                      Program of Interest
                    </label>
                    <select id="program-select" value={formData.program} onChange={e => {
                  const selected = programsForThisFlow.find(p => p.name === e.target.value);
                  updateField('program', e.target.value);
                  updateField('degreeLevel', selected?.degreeLevel || '');
                  updateField('areaOfStudy', selected?.category || '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-931" data-mp="sbq17ew8yyl6">
                      <option value="" data-id="element-932" data-mp="2hpxyd75j9iwh">-- Select Program --</option>
                      {programsForThisFlow.map(p => <option key={p.name} value={p.name} data-id="element-933" data-mp="2rhvd2nvr2szy">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 1: Program Selection */}
              {step === 1 && !university.singleProgramSelect && <div className="space-y-6" data-id="element-934" data-mp="1u1y6g3whjmgb">
                  <div data-id="element-935" data-mp="2jqv71ialhi64">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-936" data-mp="2jrensvoie0ik">
                      Degree of Interest
                    </label>
                    <select value={formData.degreeLevel} onChange={e => {
                  updateField('degreeLevel', e.target.value);
                  updateField('areaOfStudy', '');
                  updateField('program', '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-937" data-mp="2npuaeygoh6vg">
                      <option value="" data-id="element-938" data-mp="3q549cipl2cp2">-- Select Degree of Interest --</option>
                      {university.degreeLevels.map(level => <option key={level} value={level} data-id="element-939" data-mp="19k8yebskdtsj">
                          {level}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-940" data-mp="zupgp9gl85z9">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-941" data-mp="zv7l4tdelqi9">
                      Area of Study
                    </label>
                    <select value={formData.areaOfStudy} onChange={e => {
                  updateField('areaOfStudy', e.target.value);
                  updateField('program', '');
                }} disabled={!formData.degreeLevel} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-942" data-mp="vqrjx5eqj79d">
                      <option value="" data-id="element-943" data-mp="1ba43omptgja3">-- Select Area of Study --</option>
                      {uniqueAreas.map(area => <option key={area} value={area} data-id="element-944" data-mp="11d7t8bgcxn2w">
                          {area}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-945" data-mp="sdkq9l7zc97q">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-946" data-mp="see2h1ppjqx9">
                      Program
                    </label>
                    <select value={formData.program} onChange={e => updateField('program', e.target.value)} disabled={!formData.areaOfStudy} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-947" data-mp="wghdjp8gsti0">
                      <option value="" data-id="element-948" data-mp="yv5v4r97d5p9">-- Select Program --</option>
                      {availablePrograms.map(p => <option key={p.name} value={p.name} data-id="element-949" data-mp="17uj4ljxbtu9z">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 2: Academic Background */}
              {step === 2 && <div className="space-y-6" data-id="element-950" data-mp="34ncj9wdo447v">
                  <div data-id="element-951" data-mp="34onnfxb0zdzi">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-952" data-mp="1ksjxcjvst4xf">
                      Highest Level of Education
                    </label>
                    <select value={formData.highestEducation} onChange={e => updateField('highestEducation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-953" data-mp="1fvtl67n46tha">
                      <option value="" data-id="element-954" data-mp="2mj8izl72uwgv">-- Please Select --</option>
                      <option value="high-school" data-id="element-955" data-mp="2hjci3utofl5j">
                        High School Diploma/GED
                      </option>
                      <option value="some-college" data-id="element-956" data-mp="315xkttosxlqf">Some College</option>
                      <option value="associates" data-id="element-957" data-mp="364il1t4205ev">Associate's Degree</option>
                      <option value="bachelors" data-id="element-958" data-mp="361mmc2kdtcpd">Bachelor's Degree</option>
                      <option value="some-high-school" data-id="element-959" data-mp="2r6ggbfladdw1">Some High School</option>
                    </select>
                  </div>

                  <div data-id="element-960" data-mp="2ieejrsec8hr1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-961" data-mp="2ie4pvtq69qg2">
                      When would you like to start classes?
                    </label>
                    <select value={formData.startDate} onChange={e => updateField('startDate', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-962" data-mp="28g8jtgc3dlot">
                      <option value="" data-id="element-963" data-mp="8bzwvukt8d0">-- Please Select --</option>
                      <option value="immediately" data-id="element-964" data-mp="6is3d888i3f">Immediately</option>
                      <option value="1-3-months" data-id="element-965" data-mp="3rg7po0z7yxua">1-3 months</option>
                      <option value="3-6-months" data-id="element-966" data-mp="sxspllqtab5j">3-6 months</option>
                      <option value="6-12-months" data-id="element-967" data-mp="sv6fbecjdhjm">6-12 months</option>
                      <option value="1-year-plus" data-id="element-968" data-mp="nypizwv6nwvg">1+ year</option>
                      <option value="undecided" data-id="element-969" data-mp="iyjwev3rt0k8">Undecided</option>
                    </select>
                  </div>

                  <div data-id="element-970" data-mp="ewgblfv7ojma">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-971" data-mp="ex86spkwcfc1">
                      High School Graduation Year
                    </label>
                    <select value={formData.hsGradYear} onChange={e => updateField('hsGradYear', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-972" data-mp="2zngzo884q5dh">
                      <option value="" data-id="element-973" data-mp="22x3izg471v08">-- Please Select --</option>
                      {years.map(year => <option key={year} value={year} data-id="element-974" data-mp="2bvyn8mnogv0w">
                          {year}
                        </option>)}
                      <option value="1989" data-id="element-975" data-mp="ck4lpiq19w5a">1989 or earlier</option>
                    </select>
                  </div>
                </div>}

              {/* Step 3: Location */}
              {step === 3 && <div className="space-y-6" data-id="element-976" data-mp="3if890g724abe">
                  {formData.city && formData.state ? <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-id="element-977" data-mp="3dg518nzozpzq">
                      <span className="text-green-600 text-xl" data-id="element-978" data-mp="3nc8fgs7tjf31">📍</span>
                      <div className="flex-1" data-id="element-979" data-mp="3nczxt1cengbd">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-980" data-mp="2zm4aohiw99ag">
                          {formData.city}, {formData.state} {formData.zipCode}
                        </p>
                      </div>
                      <button type="button" onClick={() => {
                  updateField('city', '');
                  updateField('state', '');
                  updateField('zipCode', '');
                }} className="text-sm text-green-600 hover:text-green-700 font-semibold" data-id="element-981" data-mp="39cp5ruzczvom">
                        change
                      </button>
                    </div> : <div data-id="element-982" data-mp="w6itf7ni6vuw">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-983" data-mp="w7txl8kv25mj">
                        ZIP Code
                      </label>
                      <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  updateField('zipCode', val);
                }} placeholder="e.g. 90210" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-984" data-mp="mf64oox04lt3" />
                    </div>}

                  {formData.city && formData.state && <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-985" data-mp="1hpayuvbjqffw">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-986" data-mp="1dng1g1wqkm7e">
                        Street Address
                      </label>
                      <input type="text" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} placeholder="123 Main St" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-987" data-mp="18ms0anesmozg" />
                    </div>}
                </div>}

              {/* Step 4: Contact & Submit */}
              {step === 4 && <div className="space-y-6" data-id="element-988" data-mp="3feg5h8d5tpvd">
                  <div className="grid grid-cols-2 gap-4" data-id="element-989" data-mp="2ine9xh2t5925">
                    <div data-id="element-990" data-mp="2mny6ojjgyanr">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-991" data-mp="2mn69536ba2uu">
                        First Name
                      </label>
                      <input type="text" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-992" data-mp="2rp40kkov83yv" />
                    </div>
                    <div data-id="element-993" data-mp="11yiepeia6jgm">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-994" data-mp="11y8pht528gbz">
                        Last Name
                      </label>
                      <input type="text" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-995" data-mp="1botsrtvoi918" />
                    </div>
                  </div>

                  <div data-id="element-996" data-mp="26xf8mf84o1ow">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-997" data-mp="26zrq6r9z4a1f">
                      Email Address
                    </label>
                    <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-998" data-mp="1x72jsata5mcy" />
                  </div>

                  <div data-id="element-999" data-mp="38namcc8poxom">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1000" data-mp="38nkg8awvnozl">
                      Phone Number
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => updateField('phone', formatPhoneNumber(e.target.value))} placeholder="555-123-4567" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1001" data-mp="f4xuigy1t0gm" />
                  </div>

                  <div className="pt-4 space-y-3" data-id="element-1002" data-mp="1bw04hd95djtd">
                    {university.tcpaTextSms && <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1003" data-mp="39oe2ic5tpkvc">
                        <input type="checkbox" checked={formData.consentSms} onChange={e => updateField('consentSms', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-1004" data-mp="365otdvd6t5wn" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-1005" data-mp="3k5g15ed41q27">
                          {university.tcpaTextSms}
                        </span>
                      </label>}
                    {university.consentOnSubmit ?
                // Consent is given by clicking the submit button, so the
                // disclosure is shown as plain text. Jornaya's
                // "no interaction" pattern: a hidden input carrying the
                // disclosure id, inside a label wrapping the text.
                <label className="block text-xs text-gray-600 leading-relaxed p-4 rounded-lg border border-gray-200 bg-gray-50" data-id="element-1006" data-mp="265l9gd3f4wjs">
                        <input type="hidden" id="leadid_tcpa_disclosure" data-id="element-1007" data-mp="1s50u5qwppiv1" />
                        {university.tcpaText}
                      </label> : <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1008" data-mp="1iepkdnqckfg8">
                        {/* Jornaya's checkbox disclosure pattern. */}
                        <input id="leadid_tcpa_disclosure" type="checkbox" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-1009" data-mp="18fhz8eddfgik" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-1010" data-mp="twfdt9c66bt8">
                          {university.tcpaText || `By checking this box, I consent to receive calls and text messages from ${university.name} at the number provided above regarding educational opportunities. I understand that these calls may be generated using an automated technology and that my consent is not required to enroll. Message and data rates may apply.`}
                        </span>
                      </label>}
                  </div>
                </div>}

              {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1011" data-mp="1hkqcq6yb21ev">
                  <p className="text-red-600 text-sm" data-id="element-1012" data-mp="3c0ooaitxomvk">{error}</p>
                </div>}

              <div className="mt-8" data-id="element-1013" data-mp="3gzrva8th5366">
                {step < 4 ? <button onClick={handleContinue} disabled={addressChecking} className="w-full py-4 font-bold rounded-lg text-lg transition-colors shadow-md hover:shadow-lg disabled:cursor-wait disabled:opacity-70" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-1014" data-mp="3gxx6ukizusxc">
                    {addressChecking ? 'Checking your address…' : 'Continue'}
                  </button> : <button onClick={handleSubmit} disabled={isSubmitting || !university.consentOnSubmit && !formData.consent || Boolean(university.tcpaTextSms) && !formData.consentSms} className="w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-1015" data-mp="36zhhalzm7rpy">
                    {isSubmitting ? 'Submitting...' : university.submitLabel || 'Submit Application'}
                  </button>}

                {/* Back sits beneath the primary action: it's the secondary
                 path, and keeping it here removes the gap that opened up
                 between the heading and the first question. */}
                {step > firstStep && <button onClick={() => {
                setStep(step - 1);
                setError('');
              }} className="mt-4 mx-auto flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors" data-id="element-1016" data-mp="1u323t2hh778b">
                    <ArrowLeftIcon className="w-4 h-4" data-id="element-1017" data-mp="3elcnqxasp1b2" />
                    <span className="text-sm font-medium" data-id="element-1018" data-mp="3emnospcuvuyh">Back</span>
                  </button>}

                {university.formFootnote && <p className="mt-4 text-sm text-gray-600 leading-relaxed text-center" data-id="element-1019" data-mp="a6mmb2qothu6">
                    {university.formFootnote}
                  </p>}

                {/* Paid-placement disclosure, kept quiet so it reads as a
                 label rather than competing with the form's content. */}
                <p className="mt-4 text-right text-[11px] text-gray-400" data-id="element-1020" data-mp="63y9yl2rlssg">
                  Sponsored Ad
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* /HERO */}

        {/* Supporting site content — renders on every step so the
            program page (deep link) reads as a full marketing site,
            not just a form. */}
        {university.landingContent && <>
            {/* Stats strip */}
            {university.landingContent.stats && <section className="bg-white border-b border-gray-100 py-10 md:py-14 px-4" data-id="element-1021" data-mp="2sal13e0tdm6c">
                <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-1022" data-mp="2neds1cd4fuk6">
                  {university.landingContent.stats.map(s => <div key={s.label} className="text-center" data-id="element-1023" data-mp="1upyhp34xq0f6">
                      <div className="text-3xl md:text-4xl font-extrabold mb-1" style={{
                color: university.primaryColor
              }} data-id="element-1024" data-mp="29dtdise8okwh">
                        {s.value}
                      </div>
                      <div className="text-xs md:text-sm font-semibold uppercase tracking-wider text-gray-500" data-id="element-1025" data-mp="1byp3who4sphs">
                        {s.label}
                      </div>
                    </div>)}
                </div>
              </section>}

            {/* Why us — feature grid */}
            {/* Why-this-school: an editorial statement on the left carries the
             argument, with the supporting points as a scannable divided list
             on the right — so one idea leads and the rest support it. */}
            <section className="bg-white py-16 md:py-24 px-4" data-id="element-1026" data-mp="19dotx3t6dw6r">
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-1027" data-mp="1ec9u538fgfv7">
                <div data-id="element-1028" data-mp="1j4tbmba8jv5a">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{
                color: university.primaryColor
              }} data-id="element-1029" data-mp="1j4jsn7nlyo93">
                    About {university.name}
                  </p>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight" data-id="element-1030" data-mp="1x31e0yq1jonu">
                    Education built around your career goals.
                  </h2>
                  <div className="w-12 h-1 rounded-full my-6" style={{
                backgroundColor: university.ctaColor
              }} data-id="element-1031" data-mp="3dk8fs0dubam7" />
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-1032" data-mp="38mzwfwgyh0jd">
                    {university.mission}
                  </p>
                  <button onClick={() => window.scrollTo({
                top: 0,
                behavior: 'smooth'
              })} className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 font-bold text-sm uppercase tracking-wider rounded-md transition-colors" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-1033" data-mp="54gd5jg9950p">
                    Get Started
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1034" data-mp="j2d8ydmvx7eb" />
                  </button>
                </div>

                <ul className="divide-y divide-gray-200 border-y border-gray-200" data-id="element-1035" data-mp="10shr0zby7ul0">
                  {university.landingContent.features.map(f => {
                const Icon = featureIconMap[f.icon] || GraduationCapIcon;
                return <li key={f.title}
                // Single-line points center against their icon; points
                // with a description align to the top instead.
                className={`flex gap-4 py-5 ${f.description ? 'items-start' : 'items-center'}`} data-id="element-1036" data-mp="ms6v3hvs7psz">
                        <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: `${university.ctaColor}1f`
                  }} data-id="element-1037" data-mp="19przye1an7mr">
                          <Icon className="w-5 h-5" style={{
                      color: university.primaryColor
                    }} data-id="element-1038" data-mp="1xfcnla8e6t2d" />
                        </span>
                        <div data-id="element-1039" data-mp="3l54u7422jn9d">
                          <h3 className="text-base font-semibold text-gray-900" data-id="element-1040" data-mp="3p4dny8x3zl4x">
                            {f.title}
                          </h3>
                          {f.description && <p className="text-sm text-gray-600 leading-relaxed mt-1" data-id="element-1041" data-mp="2y70nv1mh104">
                              {f.description}
                            </p>}
                        </div>
                      </li>;
              })}
                </ul>
              </div>
            </section>

            {/* Info sections — alternating layout */}
            {university.landingContent.infoSections && university.landingContent.infoSections.map((s, i) => <section key={s.heading} className={`py-16 md:py-24 px-4 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-id="element-1042" data-mp="qo18fy733zdi" data-mpi={i}>
                  <div className="max-w-5xl mx-auto" data-id="element-1043" data-mp="2hs2vzwpyafh5">
                    {s.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
              color: university.ctaColor
            }} data-id="element-1044" data-mp="2wgpnsxwrkzp9">
                        {s.eyebrow}
                      </p>}
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5 max-w-3xl" data-id="element-1045" data-mp="31eit7oq8tpoh">
                      {s.heading}
                    </h2>
                    <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 max-w-3xl" data-id="element-1046" data-mp="3afpbo3cgdakd">
                      {s.body}
                    </p>
                    {s.bullets && <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl" data-id="element-1047" data-mp="1g2r25lykuhy3">
                        {s.bullets.map(b => <li key={b} className="flex items-start gap-3 text-gray-700" data-id="element-1048" data-mp="3rq2uyhvs23xe">
                            <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                  color: university.ctaColor
                }} data-id="element-1049" data-mp="3hyq5fwxgbpuj" />
                            <span className="text-sm md:text-base" data-id="element-1050" data-mp="3dy6a8ywfuwb1">{b}</span>
                          </li>)}
                      </ul>}
                  </div>
                </section>)}

            {/* Community image — partner-supplied lifestyle photo */}
            {university.landingContent.communityImage && <section className="bg-white py-16 md:py-24 px-4" data-id="element-1051" data-mp="128dbhz42711s">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center" data-id="element-1052" data-mp="127kehpuwjpwu">
                  <div className="order-2 md:order-1" data-id="element-1053" data-mp="2y95p9dsg1t4k">
                    {university.landingContent.communityImage.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
                color: university.ctaColor
              }} data-id="element-1054" data-mp="2paahhx9r4az5">
                        {university.landingContent.communityImage.eyebrow}
                      </p>}
                    {university.landingContent.communityImage.heading && <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-1055" data-mp="26kke4lrxagvm">
                        {university.landingContent.communityImage.heading}
                      </h2>}
                    {university.landingContent.communityImage.body && <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-1056" data-mp="1rntkkt4xjzdx">
                        {university.landingContent.communityImage.body}
                      </p>}
                  </div>
                  <div className="order-1 md:order-2 flex justify-center" data-id="element-1057" data-mp="1rhkb5oo45hxv">
                    <img src={university.landingContent.communityImage.url} alt={university.landingContent.communityImage.alt || university.name} loading="lazy" decoding="async" className="w-full max-w-md object-contain" data-id="element-1058" data-mp="1rf7tzdq6jblv" />
                  </div>
                </div>
              </section>}

            {/* Testimonial */}
            {university.landingContent.testimonial && <section className="py-16 md:py-24 px-4" style={{
          backgroundColor: `${university.primaryColor}08`
        }} data-id="element-1059" data-mp="g01c88hkg154">
                <div className="max-w-3xl mx-auto text-center" data-id="element-1060" data-mp="cyg1mqo33903">
                  <div className="text-6xl leading-none mb-4 font-serif" style={{
              color: university.primaryColor
            }} data-id="element-1061" data-mp="80ocg4etq0zl">
                    "
                  </div>
                  <blockquote className="text-xl md:text-2xl font-medium text-gray-900 leading-relaxed mb-6" data-id="element-1062" data-mp="3q6suy09a3bkw">
                    {university.landingContent.testimonial.quote}
                  </blockquote>
                  <div className="text-sm text-gray-600" data-id="element-1063" data-mp="40dwq018bw7j">
                    <span className="font-bold text-gray-900" data-id="element-1064" data-mp="3xrpy2m5xkqd">
                      {university.landingContent.testimonial.name}
                    </span>
                    {' — '}
                    {university.landingContent.testimonial.program}
                  </div>
                </div>
              </section>}

            {/* Career section with image */}
            {university.landingContent.careerSection && <section className="bg-white py-16 md:py-24 px-4" data-id="element-1065" data-mp="1mz21wtq7kgq4">
                <div className="max-w-6xl mx-auto rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-1066" data-mp="1mw63736jdo0m">
                  <div className="grid grid-cols-1 md:grid-cols-2 items-stretch" data-id="element-1067" data-mp="1d0cvbx3riip6">
                    <div className="p-8 md:p-12 flex flex-col justify-center md:order-2" data-id="element-1068" data-mp="1d262ddiaf4xp">
                      <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-1069" data-mp="20s0d9pj1qjfb">
                        {university.landingContent.careerSection.headline}
                      </h2>
                      <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6" data-id="element-1070" data-mp="1rvsqk5g2wkn5">
                        {university.landingContent.careerSection.body}
                      </p>
                      <button onClick={() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }} className="self-start px-7 py-3 text-white font-bold rounded-lg transition-all shadow-md hover:shadow-lg" style={{
                  backgroundColor: university.ctaColor
                }} data-id="element-1071" data-mp="2jmotoecc7ax8">
                        Get Started
                      </button>
                    </div>
                    <div className="min-h-[280px] md:min-h-0 md:order-1" data-id="element-1072" data-mp="2dowj6ybmc9vd">
                      <img src={university.landingContent.careerSection.imageUrl} alt={university.landingContent.careerSection.imageAlt || university.name} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-1073" data-mp="2dmk6azkpwpp6" />
                    </div>
                  </div>
                </div>
              </section>}

            {/* Final CTA band */}
            {university.landingContent.finalCta && <section className="py-16 md:py-20 px-4" style={{
          backgroundColor: university.primaryColor
        }} data-id="element-1074" data-mp="3dwjpt53e30ux">
                <div className="max-w-4xl mx-auto text-center text-white" data-id="element-1075" data-mp="3nn61wpbwd98v">
                  <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4" data-id="element-1076" data-mp="3ro80r51wjjab">
                    {university.landingContent.finalCta.headline}
                  </h2>
                  <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-1077" data-mp="fu66ewj679i">
                    {university.landingContent.finalCta.body}
                  </p>
                  <button onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }} className="px-8 py-4 font-bold rounded-lg text-base md:text-lg transition-all shadow-lg hover:shadow-xl" style={{
              backgroundColor: university.ctaColor,
              color: '#fff'
            }} data-id="element-1078" data-mp="22b4k2wrd3qbq">
                    {university.landingContent.finalCta.buttonLabel || 'Request Information'}
                  </button>
                </div>
              </section>}
          </>}

        {/* Branded footer */}
        <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs" data-id="element-1079" data-mp="21lovco614gs3">
          <div className="max-w-5xl mx-auto space-y-2" data-id="element-1080" data-mp="26ks007i3kkzj">
            <p data-id="element-1081" data-mp="26neaog0abglz">
              © {new Date().getFullYear()} {university.name}. All rights
              reserved.
            </p>
            <p className="text-gray-500" data-id="element-1082" data-mp="v6xrcblm0ftu">
              {university.accreditation} • Founded {university.founded}
            </p>
            <div className="max-w-2xl mx-auto pt-2" data-id="element-1083" data-mp="39cw0uh0reuqi">
              <TrackingNoticeLinks data-id="element-1084" data-mp="34fcjh88rm1uc" />
            </div>
          </div>
        </footer>
      </main>
    </div>;
}